import { useCallback, useEffect, useRef, useState } from 'react';
import { taskApi, type ReportAnswer, type ReportContext, type ReportQuestionItem } from '@/axios/company';

/** 回答弹窗仅保留当前选择的临时状态，正文不进入列表或持久缓存。 */
export interface ReportAnswerState {
  open: boolean;
  status: 'idle' | 'loading' | 'success' | 'network-error' | 'unavailable';
  data?: ReportAnswer;
}

/**
 * 管理回答加载与失效，阻止关闭、翻页和切换问题后的迟到响应覆盖。
 * @param {ReportContext} context 列表、统计和详情共享的报告上下文。
 * @param {string} selectionKey 分页和筛选条件的稳定标识。
 * @returns {{state: ReportAnswerState, open: (row: ReportQuestionItem) => void, close: () => void, retry: () => void}} 弹窗状态和交互方法。
 */
export default function useReportAnswer(context: ReportContext, selectionKey: string) {
  const [state, setState] = useState<ReportAnswerState>({ open: false, status: 'idle' });
  const requestId = useRef(0);
  const selected = useRef<{ context: ReportContext; wordId: string }>();

  /**
   * 清空正文并使当前请求失效。
   * @returns {void} 弹窗关闭且没有已缓存正文。
   */
  const close = useCallback((): void => {
    requestId.current += 1;
    selected.current = undefined;
    setState({ open: false, status: 'idle' });
  }, []);

  /**
   * 按当前选择读取正文，每次加载先删除上一条正文。
   * @returns {Promise<void>} 当前请求处理完成，失效响应直接丢弃。
   */
  const load = useCallback(async (): Promise<void> => {
    const selection = selected.current;
    if (!selection) {
      return;
    }
    const currentId = ++requestId.current;
    setState({ open: true, status: 'loading' });
    try {
      const data = await taskApi.analysisAnswer({ ...selection.context, wordId: selection.wordId });
      if (currentId === requestId.current) {
        setState({ open: true, status: 'success', data });
      }
    } catch (error) {
      if (currentId !== requestId.current) {
        return;
      }
      const failure = error as { err_no?: number };
      setState({ open: true, status: failure?.err_no !== undefined ? 'unavailable' : 'network-error' });
    }
  }, []);

  /**
   * 选择问题并打开弹窗，复制上下文保证重试查询同一份报告。
   * @param {ReportQuestionItem} row 当前页被选中的真实提问。
   * @returns {void} 发起一次按需详情读取。
   */
  const open = useCallback((row: ReportQuestionItem): void => {
    selected.current = { context: { ...context }, wordId: row.id };
    void load();
  }, [context.id, context.source, load]);

  /**
   * 手动重试网络异常，不自动循环请求。
   * @returns {void} 使用原报告和问题重新读取详情。
   */
  const retry = useCallback((): void => { void load(); }, [load]);

  useEffect(() => {
    close();
    // 卸载和条件变化均递增标识，正文响应不会落入新上下文。
    return () => { requestId.current += 1; };
  }, [context.id, context.source, selectionKey, close]);

  return { state, open, close, retry };
}
